import { warnOnce } from '@/lib/warnOnce';

/**
 * `options.badgeStyles` -> the style string for a room's comfort value.
 *
 * **This file exists because of W-1** (docs/MIGRATION-WIDGETS.md §8). The legacy widget built a
 * JavaScript expression out of the configured operator and the two values and ran it through
 * `eval`:
 *
 * ```js
 * const condition = `'${badgeStyle.value}' ${badgeStyle.condition} '${value}'`;
 * if (eval(condition)) return badgeStyle.style;
 * ```
 *
 * `value` there is the comfort index taken straight out of an MQTT payload, so anything able
 * to publish to the topic could close the quote and run arbitrary code on a wall-mounted
 * display — a code-execution path from the broker, not a style question. The operator is
 * looked up in a table now, and nothing is ever compiled.
 *
 * The comparison stays a **string** comparison, which is what `eval` was doing: both operands
 * were interpolated between single quotes, so `'90' < '100'` was false there and is false
 * here. All three deployed configs use `===` only.
 */

export type BadgeStyle = {
  /** The left operand — the value this style matches. */
  value: string;
  /** The operator. `===` in every deployed config. */
  condition: string;
  /** A raw CSS declaration string, parsed by `parseStyleString`. */
  style: string;
};

/**
 * The operators `eval` could have evaluated, restricted to the six that are comparisons.
 *
 * Anything else — `=`, `==`, an empty string, or something that was never an operator — is a
 * config mistake. Legacy would have thrown a `SyntaxError` out of `eval` and taken the whole
 * widget's render with it; here it warns once and the entry simply never matches.
 */
const COMPARATORS: Record<string, (left: string, right: string) => boolean> = {
  '===': (left, right) => left === right,
  '!==': (left, right) => left !== right,
  '<': (left, right) => left < right,
  '>': (left, right) => left > right,
  '<=': (left, right) => left <= right,
  '>=': (left, right) => left >= right,
};

/**
 * The `style` of the first matching badge, or `''` when nothing matches.
 *
 * `''` is a real state, not an error: it is what every room shows before the first message,
 * when the comfort index is the literal `'N/A'` and no configured badge value equals it
 * (docs/MIGRATION-WIDGETS.md §9).
 */
export function resolveBadgeStyle(badgeStyles: readonly BadgeStyle[], value: string): string {
  for (const badgeStyle of badgeStyles) {
    const compare = COMPARATORS[badgeStyle.condition];
    if (compare === undefined) {
      warnOnce(
        `badge-condition:${badgeStyle.condition}`,
        '[signage] FloorOccupancyWidget badgeStyles condition "%s" is not one of %s; ' +
          'this badge style can never match.',
        badgeStyle.condition,
        Object.keys(COMPARATORS).join(' '),
      );
      continue;
    }
    if (compare(badgeStyle.value, value)) return badgeStyle.style;
  }
  return '';
}
